import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Search, MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerSegments = () => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentSegment, setCurrentSegment] = useState(null);
  const [segmentCustomers, setSegmentCustomers] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria: {
      purchase_count_min: null,
      purchase_count_max: null,
      total_spent_min: null,
      total_spent_max: null,
      email_subscribed: null,
      tags: []
    }
  });

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/customer-segments/`);
      setSegments(response.data);
    } catch (error) {
      console.error('Error fetching segments:', error);
      toast.error('Failed to load customer segments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSegmentCustomers = async (segmentId) => {
    try {
      const response = await axios.get(`${API_URL}/api/customer-segments/${segmentId}/customers`);
      setSegmentCustomers(response.data);
    } catch (error) {
      console.error('Error fetching segment customers:', error);
      toast.error('Failed to load segment customers');
    }
  };

  const handleCreateSegment = async () => {
    try {
      // Clean up criteria (remove null values)
      const cleanedCriteria = Object.entries(formData.criteria).reduce((acc, [key, value]) => {
        if (value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      await axios.post(`${API_URL}/api/customer-segments/`, {
        name: formData.name,
        description: formData.description,
        criteria: cleanedCriteria
      });
      
      toast.success('Segment created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      fetchSegments();
    } catch (error) {
      console.error('Error creating segment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create segment');
    }
  };

  const handleDeleteSegment = async (segmentId) => {
    if (!window.confirm('Are you sure you want to delete this segment?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/customer-segments/${segmentId}`);
      toast.success('Segment deleted successfully');
      fetchSegments();
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete segment');
    }
  };

  const handleRecalculate = async (segmentId) => {
    try {
      await axios.post(`${API_URL}/api/customer-segments/${segmentId}/recalculate`);
      toast.success('Segment recalculated successfully');
      fetchSegments();
    } catch (error) {
      console.error('Error recalculating segment:', error);
      toast.error('Failed to recalculate segment');
    }
  };

  const handleViewSegment = async (segment) => {
    setCurrentSegment(segment);
    setIsViewModalOpen(true);
    await fetchSegmentCustomers(segment.id);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      criteria: {
        purchase_count_min: null,
        purchase_count_max: null,
        total_spent_min: null,
        total_spent_max: null,
        email_subscribed: null,
        tags: []
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSegments(segments.map(s => s.id));
    } else {
      setSelectedSegments([]);
    }
  };

  const handleSelectSegment = (segmentId, checked) => {
    if (checked) {
      setSelectedSegments([...selectedSegments, segmentId]);
    } else {
      setSelectedSegments(selectedSegments.filter(id => id !== segmentId));
    }
  };

  const filteredSegments = segments.filter(segment =>
    segment.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Segments</h1>
            <p className="text-gray-500 mt-1">Create and manage customer segments for targeted marketing</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create segment
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search segments"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-md"
        />
      </div>

      {/* Segments List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">Loading segments...</div>
          ) : filteredSegments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No segments found. Create your first segment to get started!
            </div>
          ) : (
            <div className="divide-y">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 text-sm font-medium text-gray-600">
                <div className="col-span-1 flex items-center">
                  <Checkbox
                    checked={selectedSegments.length === segments.length}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
                <div className="col-span-5">Name</div>
                <div className="col-span-2 text-right">% of customers</div>
                <div className="col-span-3">Last activity</div>
                <div className="col-span-1"></div>
              </div>

              {/* Table Rows */}
              {filteredSegments.map((segment) => (
                <div
                  key={segment.id}
                  className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 items-center"
                >
                  <div className="col-span-1 flex items-center">
                    <Checkbox
                      checked={selectedSegments.includes(segment.id)}
                      onCheckedChange={(checked) => handleSelectSegment(segment.id, checked)}
                    />
                  </div>
                  <div className="col-span-5">
                    <button
                      onClick={() => handleViewSegment(segment)}
                      className="text-left hover:underline font-medium"
                    >
                      {segment.name}
                    </button>
                  </div>
                  <div className="col-span-2 text-right">
                    {segment.percentage_of_total}%
                  </div>
                  <div className="col-span-3 text-gray-600">
                    Created on {new Date(segment.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewSegment(segment)}>
                          View customers
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleRecalculate(segment.id)}>
                          Recalculate
                        </DropdownMenuItem>
                        {!segment.is_system_segment && (
                          <DropdownMenuItem
                            onClick={() => handleDeleteSegment(segment.id)}
                            className="text-red-600"
                          >
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Segment Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Customer Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Segment Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., High-value customers"
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this segment..."
                rows={3}
              />
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">Segment Criteria</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchase_min">Min Purchases</Label>
                  <Input
                    id="purchase_min"
                    type="number"
                    value={formData.criteria.purchase_count_min || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        purchase_count_min: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                    placeholder="e.g., 1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="purchase_max">Max Purchases</Label>
                  <Input
                    id="purchase_max"
                    type="number"
                    value={formData.criteria.purchase_count_max || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        purchase_count_max: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                    placeholder="e.g., 10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="spent_min">Min Amount Spent ($)</Label>
                  <Input
                    id="spent_min"
                    type="number"
                    value={formData.criteria.total_spent_min || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        total_spent_min: e.target.value ? parseFloat(e.target.value) : null
                      }
                    })}
                    placeholder="e.g., 100"
                  />
                </div>
                
                <div>
                  <Label htmlFor="spent_max">Max Amount Spent ($)</Label>
                  <Input
                    id="spent_max"
                    type="number"
                    value={formData.criteria.total_spent_max || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      criteria: {
                        ...formData.criteria,
                        total_spent_max: e.target.value ? parseFloat(e.target.value) : null
                      }
                    })}
                    placeholder="e.g., 1000"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="email_subscribed"
                  checked={formData.criteria.email_subscribed === true}
                  onCheckedChange={(checked) => setFormData({
                    ...formData,
                    criteria: {
                      ...formData.criteria,
                      email_subscribed: checked ? true : null
                    }
                  })}
                />
                <Label htmlFor="email_subscribed" className="cursor-pointer">
                  Email subscribers only
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateSegment} disabled={!formData.name.trim()}>
              Create Segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Segment Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentSegment?.name}</DialogTitle>
            <p className="text-sm text-gray-500 mt-2">
              {currentSegment?.customer_count} customers ({currentSegment?.percentage_of_total}%)
            </p>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {segmentCustomers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No customers in this segment
              </div>
            ) : (
              <div className="space-y-2">
                <h3 className="font-semibold">Customers</h3>
                <div className="border rounded-md divide-y max-h-96 overflow-y-auto">
                  {segmentCustomers.map((customer) => (
                    <div key={customer.id} className="p-3 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          <p className="text-sm text-gray-600">{customer.email}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-medium">${customer.total_spent.toFixed(2)}</p>
                          <p className="text-gray-600">{customer.purchase_count} purchases</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CustomerSegments;
